// ============================================================================
// EhrItSetupGuidePanel — SMART Build 2P
//
// In-app "IT Setup Guide" / EHR Integration Onboarding Pack. A collapsible
// section inside EhrIntegrationCard (which is already gated to platform_admin +
// tenant_admin via TenantImpactSettingsView). It renders:
//   - dynamic, connection-specific COPYABLE values (launch URL, redirect URI,
//     FHIR base URL, SMART discovery URL, client ID, scopes, mode, status), and
//   - static onboarding content (purpose, v1 scope, PHI boundary, IT/admin
//     responsibilities, sandbox + go-live checklists, troubleshooting, future).
//
// Read/copy only — no writes, no Gate/launch/import behavior change. Shows NO
// secrets, tokens, client secrets, raw fhirUser, PHI, launch state, or codes.
// Client ID is a PUBLIC PKCE value and is safe to display/copy.
// ============================================================================

import React, { useState } from 'react';
import {
  BookOpen, ChevronDown, ChevronRight, Copy, Check, Shield, AlertTriangle, ExternalLink,
} from 'lucide-react';
import {
  buildItSetupGuideModel,
  SMART_APP_LAUNCHER_URL,
  GUIDE_PURPOSE,
  V1_SUPPORTED,
  V1_NOT_SUPPORTED,
  APP_REGISTRATION_SCOPES,
  SCOPES_POLICY_NOTE,
  FHIR_RESOURCES_READ,
  DATA_HANDLING,
  USER_IDENTITY_MAPPING,
  HOSPITAL_IT_RESPONSIBILITIES,
  REFERRALGATE_ADMIN_RESPONSIBILITIES,
  SANDBOX_TEST_CHECKLIST,
  PRODUCTION_GOLIVE_CHECKLIST,
  TROUBLESHOOTING,
  FUTURE_EXTENSIONS,
  INTEGRATION_BOUNDARY_NOTE,
} from '../core/ehrItSetupGuide.js';

// ── small presentational helpers ──────────────────────────────────────────────

const SubHeading = ({ children }) => (
  <div className="text-[11px] font-semibold text-slate-200 uppercase tracking-wide mb-1.5">{children}</div>
);

const Bullets = ({ items }) => (
  <ul className="text-[11px] text-slate-400 space-y-0.5 list-disc list-inside">
    {items.map((s) => <li key={s}>{s}</li>)}
  </ul>
);

const CheckItems = ({ items }) => (
  <ul className="text-[11px] text-slate-400 space-y-0.5">
    {items.map((s) => <li key={s}>☐ {s}</li>)}
  </ul>
);

const OrderedItems = ({ items }) => (
  <ol className="text-[11px] text-slate-400 space-y-0.5 list-decimal list-inside">
    {items.map((s) => <li key={s}>{s}</li>)}
  </ol>
);

const Section = ({ n, title, children }) => (
  <div className="pt-3 border-t border-slate-700/40">
    <SubHeading>{n}. {title}</SubHeading>
    {children}
  </div>
);

// ── main panel ────────────────────────────────────────────────────────────────

const EhrItSetupGuidePanel = ({ tenantId, connection = null }) => {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState('');

  const model = buildItSetupGuideModel({ tenantId, connection });

  const copy = async (key, value) => {
    if (!value) return;
    try {
      if (navigator?.clipboard?.writeText) await navigator.clipboard.writeText(value);
      setCopied(key);
      setTimeout(() => setCopied((k) => (k === key ? '' : k)), 1500);
    } catch (_) { /* clipboard unavailable — value remains visible to copy manually */ }
  };

  const CopyField = ({ label, value, fieldKey, hint }) => (
    <div>
      <div className="text-[11px] font-medium text-slate-300 mb-1">
        {label}{hint && <span className="ml-2 text-slate-500 font-normal">{hint}</span>}
      </div>
      <div className="flex items-stretch gap-2">
        <code className="flex-1 min-w-0 px-2 py-1.5 bg-slate-900/60 border border-slate-700 rounded text-[11px] text-blue-700 break-all">
          {value || 'Not configured yet'}
        </code>
        <button
          type="button"
          onClick={() => copy(fieldKey, value || '')}
          disabled={!value}
          className="shrink-0 inline-flex items-center gap-1 px-2 py-1 text-[11px] rounded border border-slate-600 text-slate-200 hover:bg-slate-800 disabled:opacity-40"
        >
          {copied === fieldKey ? <Check className="w-3 h-3 text-emerald-400" /> : <Copy className="w-3 h-3" />}
          {copied === fieldKey ? 'Copied' : 'Copy'}
        </button>
      </div>
    </div>
  );

  return (
    <div className="mt-5 pt-4 border-t border-slate-700/50">
      {/* Collapsible header */}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="w-full flex items-center gap-2 text-left"
      >
        {open ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
        <BookOpen className="w-4 h-4 text-sky-400" />
        <h3 className="text-sm font-semibold text-white">IT Setup Guide</h3>
        <span className="ml-1 px-2 py-0.5 rounded text-[10px] font-medium bg-sky-500/15 text-blue-700">
          {model.modeLabel}
        </span>
        {model.status && (
          <span className="px-2 py-0.5 rounded text-[10px] font-medium bg-slate-600/40 text-slate-300">
            {model.status}
          </span>
        )}
      </button>
      <p className="text-[11px] text-slate-400 mt-1 ml-6">
        EHR Integration Onboarding Pack — copyable setup values + checklists for hospital IT. No tokens, secrets, or PHI are shown.
      </p>

      {open && (
        <div className="mt-3 space-y-3">
          {/* Boundary banner */}
          <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
            <AlertTriangle className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
            <p className="text-[11px] text-amber-300">{INTEGRATION_BOUNDARY_NOTE}</p>
          </div>

          {/* ── Dynamic, connection-specific copyable values ── */}
          <div className="p-3 rounded-lg bg-slate-900/30 border border-slate-700/40 space-y-3">
            <div className="flex items-center gap-2">
              <Shield className="w-3.5 h-3.5 text-sky-400" />
              <SubHeading>Connection values to register</SubHeading>
            </div>
            {!model.hasConnection && (
              <p className="text-[11px] text-amber-300">
                No connection configured yet. Save a connection above to populate the launch URL, FHIR base URL, client ID, and scopes.
              </p>
            )}
            <CopyField label="App name" value={model.appName} fieldKey="appName" />
            <CopyField label="Launch URL (register in EHR / launcher)" value={model.launchUrl} fieldKey="launchUrl" />
            <CopyField label="Redirect URI (callback)" value={model.redirectUri} fieldKey="redirectUri" />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <CopyField label="FHIR base URL" value={model.fhirBaseUrl} fieldKey="fhirBaseUrl" />
              <CopyField label="Client ID" value={model.clientId} fieldKey="clientId" hint="public" />
            </div>
            <CopyField label="SMART configuration URL" value={model.smartConfigurationUrl} fieldKey="smartConfigUrl" hint="discovery" />
            <CopyField label="Requested scopes" value={model.requestedScopes} fieldKey="scopes" />
            <p className="text-[11px] text-slate-500">
              Authorization &amp; token endpoints are discovered from the FHIR server&apos;s SMART configuration
              (<code className="text-slate-400">/.well-known/smart-configuration</code>) at launch time — they are not stored on the connection.
            </p>
            {/* Mode / auth chips */}
            <div className="flex flex-wrap gap-2 pt-1">
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium bg-sky-500/15 text-blue-700">mode: {model.modeLabel}</span>
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium bg-slate-500/15 text-slate-300">requireUserMapping: {model.requireUserMapping ? 'yes' : 'no'}</span>
              {model.authType && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium bg-slate-500/15 text-slate-300">authType: {model.authType}</span>}
              {model.tokenEndpointAuthMethod && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium bg-slate-500/15 text-slate-300">tokenAuth: {model.tokenEndpointAuthMethod}</span>}
              {model.sourceSystemLabel && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium bg-slate-500/15 text-slate-300">source: {model.sourceSystemLabel}</span>}
            </div>
          </div>

          {/* ── Static onboarding content ── */}
          <Section n={1} title="Purpose">
            <p className="text-[11px] text-slate-400">{GUIDE_PURPOSE}</p>
          </Section>

          <Section n={2} title="What ReferralGate supports in v1">
            <Bullets items={V1_SUPPORTED} />
          </Section>

          <Section n={3} title="What ReferralGate does not do yet">
            <Bullets items={V1_NOT_SUPPORTED} />
          </Section>

          <Section n={4} title="Required SMART app registration values">
            <ul className="text-[11px] text-slate-400 space-y-0.5">
              {APP_REGISTRATION_SCOPES.map((s) => (
                <li key={s.scope}><code className="text-blue-700">{s.scope}</code> — {s.purpose}</li>
              ))}
            </ul>
            <p className="text-[11px] text-slate-500 mt-1.5">{SCOPES_POLICY_NOTE}</p>
            <p className="text-[11px] text-slate-500 mt-1">
              App name, Launch URL, Redirect URI, FHIR base URL, Client ID, and the exact scope string are copyable above.
            </p>
          </Section>

          <Section n={5} title="FHIR resources read in v1">
            <div className="space-y-2">
              {FHIR_RESOURCES_READ.map((r) => (
                <div key={r.resource}>
                  <div className="text-[11px] font-medium text-slate-300">{r.resource}</div>
                  <Bullets items={r.fields} />
                </div>
              ))}
            </div>
          </Section>

          <Section n={6} title="Data handling / PHI boundary">
            <Bullets items={DATA_HANDLING} />
          </Section>

          <Section n={7} title="User identity / fhirUser mapping">
            <Bullets items={USER_IDENTITY_MAPPING} />
          </Section>

          <Section n={8} title="Hospital IT responsibilities">
            <CheckItems items={HOSPITAL_IT_RESPONSIBILITIES} />
          </Section>

          <Section n={9} title="ReferralGate admin responsibilities">
            <CheckItems items={REFERRALGATE_ADMIN_RESPONSIBILITIES} />
          </Section>

          <Section n={10} title="Sandbox test checklist">
            <OrderedItems items={SANDBOX_TEST_CHECKLIST} />
            <div className="mt-2">
              <a
                href={SMART_APP_LAUNCHER_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] rounded-lg bg-sky-600 text-white hover:bg-sky-700"
              >
                <ExternalLink className="w-3.5 h-3.5" /> Open SMART App Launcher
              </a>
            </div>
          </Section>

          <Section n={11} title="Production go-live checklist">
            <CheckItems items={PRODUCTION_GOLIVE_CHECKLIST} />
          </Section>

          <Section n={12} title="Troubleshooting">
            <ul className="text-[11px] text-slate-400 space-y-1">
              {TROUBLESHOOTING.map((t) => (
                <li key={t.symptom}>
                  <span className="text-amber-300 font-medium">{t.symptom}</span> — {t.fix}
                </li>
              ))}
            </ul>
          </Section>

          <Section n={13} title="Future optional extensions">
            <Bullets items={FUTURE_EXTENSIONS} />
          </Section>
        </div>
      )}
    </div>
  );
};

export default EhrItSetupGuidePanel;
